/**
 * ClinicalSafetyPage.tsx — the PUBLIC clinical-safety page.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: public clinical-safety
 * page (2026-08-20).
 *
 * WHY THIS PAGE EXISTS. The payment screen shows a patient what their medicine
 * can do to them before they pay (checkout/MedicationSafetyGate.tsx), and that
 * gate does its job. What it could not do was outlive the checkout. There was
 * nowhere to send anybody afterwards: not from the dispatch email that arrives
 * weeks later, when something is actually happening to them; not to the
 * clinician who wants to read what patients are being shown without walking a
 * whole purchase to see it; and not to the person who read it once, on a phone,
 * while entering their card details. The CTO asked for the missing half — "a
 * button to get more information… Read more (opens in new tab) — branded the
 * same way but with proper information covering all clinical and legal
 * aspects." This is that page, and the payment screen's "Read more" link is
 * that button.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THIS PAGE IS INFORMATION. IT MUST NEVER BECOME PROMOTION.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * READ THIS BEFORE ADDING ANYTHING TO THIS FILE. It is not a style preference
 * and it is not a decision anyone is free to revisit in a quiet UI pass. It is
 * the entire reason a page that names prescription-only medicines is allowed to
 * be public at all (CTO decision, 2026-08-20).
 *
 * UK law forbids advertising prescription-only medicines to the public. The
 * whole of the rest of this portal handles that by NEVER naming one before
 * eligibility — that is R28, and it is why the clinic journey talks in
 * categories. This page names them openly, which is permitted for information
 * about a medicine and NOT permitted for anything that promotes its supply. The
 * line between the two is not drawn by intent or by tone. It is drawn by whether
 * the page carries an offer.
 *
 * So, on this page, NONE of the following may ever appear:
 *   - a price, a discount, a saving, a plan length or any figure in pounds;
 *   - a "Start now" / "Choose your plan" / "Buy" / "Get treated" control, or any
 *     other route that begins a purchase;
 *   - an efficacy claim or a weight-loss figure — how well it works is exactly
 *     the claim that turns information into an advertisement;
 *   - comparative or persuasive wording of any kind: "best", "leading",
 *     "clinically proven", "trusted by".
 *
 * A link back to the clinic's home page is fine — the reader has to be able to
 * find out who wrote this. A button that starts a purchase is not. If you are
 * here because a growth ticket asked for a conversion control on a page with
 * traffic and no funnel, the answer is no, and the reason is above.
 *
 * AND THE RULE COVERS THE CHROME, NOT ONLY THIS FILE (2026-08-20, review). The
 * shared patient header carries the estate's primary conversion control —
 * "Speak to a clinician now", a link into the callback lane — and it mounted
 * above this page's <main> on the first cut, which is an offer on the one
 * surface whose standing depends on carrying none. `ui/chrome.tsx` now drops the
 * patient ACTIONS on this route (see `useIsClinicalSafetyRoute`) and keeps the
 * brand lockup, the emergency ribbon and the legal footer. The spec that holds
 * this reads the WHOLE document, because the one that reads `<main>` could not
 * see the control that was actually there.
 *
 * The page is also `noindex` (lib/noIndex.ts): it is a page people are SENT to,
 * never one they should be ACQUIRED through. A search listing for a POM brand
 * name pointing at the clinic that sells it is an advertisement no matter how
 * carefully the body copy is written.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *
 * IT RENDERS THE PATIENT'S OWN COMPONENTS, NOT A SECOND SET. The urgent-action
 * block, the topics, the Yellow Card sentence and the external sources all come
 * from `checkout/MedicationSafetyGate` — the same module the payment screen and
 * the clinician review surface mount. The CTO was specific that the urgent block
 * here is to be the block he already signed off, and a page that merely
 * RESEMBLED the payment screen would be a page that stops resembling it the
 * first time either side is restyled. Whatever a patient reads before paying is
 * what this page shows, by construction rather than by discipline.
 *
 * THE VERSION IS PUBLISHED; THE SIGN-OFF STATE IS NOT. Both are provenance, and
 * only one of them is the reader's business. The version is what an
 * acknowledgement is recorded against, so printing it is what lets a patient, a
 * clinician or a regulator match these words to the words a particular person
 * agreed to — a reference surface that cannot say WHICH wording it is showing is
 * not evidence of anything. `clinicallyApproved` is internal editorial state:
 * "awaiting review by our clinical lead", printed above the risks of the medicine
 * somebody is taking, reads as a warning that the safety information itself may
 * be wrong, and the patient is the reader least able to act on it. It stays on
 * the CLINICIAN surfaces, which is where contracts asks for it and where it can
 * be acted on. See the version block below for why it is a sentence and not a
 * label/value row (2026-08-20).
 *
 * FAIL LOUD, NEVER HALF-QUIET. A failed read renders an error with a retry, not
 * an empty page: "no medicines listed" and "we could not reach the server" look
 * identical when both are blank, and only one of them means there is nothing to
 * worry about. Same reasoning as the payment gate refusing the sale rather than
 * selling unexplained.
 *
 * R28: every medication name and every clinical sentence on this page arrives ON
 * THE WIRE and is rendered as given. None of it is written into this file or its
 * comments — the R28 source scan (journey/r28.spec.tsx) reads comments too.
 */
import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { getPublicClinicSafety } from '../api/safety';
import type { PublicMedicationSafety } from '../api/types';
import {
  PatientExternalSources,
  PatientSafetyTopics,
  PatientUrgentActions,
  PatientYellowCardLine,
} from '../checkout/MedicationSafetyGate';
import { useNoIndex } from '../lib/noIndex';
import { Alert, buttonClass, buttonStyle, Card, ErrorState, PageFrame, Spinner, Title } from '../ui/kit';
import { queryReducedMotion } from '../ui/motion';
import { CLINICAL_SAFETY_CLINIC_SLUG } from './route';

/** The MHRA's own reporting route, shown once at the top for the whole page as
 * well as per medicine. Hard-coded here — unlike every clinical sentence on this
 * page — because it is not clinical content: it is the UK regulator's address,
 * identical for every medicine, and a reader who arrives mid-side-effect must
 * not have to wait for a network read to find it. */
const YELLOW_CARD_URL = 'https://yellowcard.mhra.gov.uk';

type Loadable =
  | { state: 'loading' }
  | { state: 'ready'; medications: PublicMedicationSafety[] }
  | { state: 'error' };

/** A DOM id per medicine, for the contents list at the top of the page.
 * Prefixed and slugified rather than using `productKey` raw: the key is
 * server-supplied and has no promise of being a valid id or of being free of
 * characters a CSS selector would choke on. */
function sectionId(productKey: string): string {
  return `treatment-${productKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}

/**
 * The section the URL is pointing at, if any — `#treatment-<productKey>`.
 *
 * DECODED DEFENSIVELY, and that is not paranoia about a rare input. This is read
 * from a MOUNT EFFECT, and `decodeURIComponent` throws a `URIError` on a
 * malformed escape (`#%`, a hash truncated by a mail client that wrapped the
 * line, a link re-typed by hand). An unhandled throw there does not degrade the
 * page — it unmounts the tree and leaves a blank screen, on a safety page, for
 * the reader whose link was already mangled. Falling back to the raw fragment
 * simply matches nothing and leaves the page as it is, which is the honest
 * outcome: no section opens, and everything else on the page still works.
 */
function targetedSectionId(): string {
  const raw = window.location.hash.slice(1);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * True when the browser is going to open this link somewhere OTHER than the
 * document it was clicked in — a new tab (ctrl/cmd), a new window (shift), a
 * download (alt), or a non-primary mouse button.
 *
 * WHY A PAGE NEEDS THIS AT ALL. React fires `onClick` for every one of those,
 * indistinguishably from a plain press, so any handler that changes the current
 * page on click will change it for a reader who explicitly asked NOT to leave
 * where they are. On this page that reader is doing something entirely
 * reasonable — opening a second treatment beside the one they are reading — and
 * the cost of getting it wrong is that the tab behind them silently collapses
 * the medicine they had open and scrolls off their place in it.
 *
 * `event.button` is checked as well as the modifiers because it is the one that
 * survives a platform not covered above; a keyboard `Enter` on a focused link
 * arrives with no modifiers and `button === 0`, so activation by keyboard is
 * NOT filtered out here and still opens the section.
 */
function opensElsewhere(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

export function ClinicalSafetyPage() {
  useNoIndex();
  const [view, setView] = useState<Loadable>({ state: 'loading' });

  const load = useCallback(() => {
    setView({ state: 'loading' });
    getPublicClinicSafety(CLINICAL_SAFETY_CLINIC_SLUG)
      .then((response) => setView({ state: 'ready', medications: response.medications }))
      .catch(() => setView({ state: 'error' }));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PageFrame>
      <Title>Treatment safety information</Title>
      <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '15px', lineHeight: 1.65, color: 'var(--gps-text)' }}>
        This page sets out the risks, side effects and safety advice for every treatment this clinic
        prescribes. It is the same information a patient is shown and asked to confirm they have read
        before an order can be placed, published here so that it can be read at any time — before
        starting, while taking a treatment, or by a clinician or pharmacist who wants to see exactly
        what patients are told.
      </p>
      <p style={{ margin: '0 0 var(--gps-s-4)', fontSize: '15px', lineHeight: 1.65, color: 'var(--gps-text)' }}>
        It is written for information only. It is not a substitute for the advice of the prescriber
        or pharmacist looking after you, and it does not replace the patient information leaflet that
        comes with your medicine. Always read that leaflet as well.
      </p>

      {/* THE URGENT ROUTE OUT OF THIS PAGE, ABOVE EVERYTHING ELSE. Somebody may
          arrive here at 2am because something is happening to them right now.
          Emergency advice and the regulator's reporting route must not be below
          a fold, behind a read, or dependent on the API call having succeeded —
          which is why this block is rendered before the load state is even
          considered. */}
      <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-4)' }}>
        {/* NO ICON HERE. `Alert` already renders one for its tone (ui/kit.tsx —
            the `t.icon` span), so adding a second painted two warning triangles
            side by side on the one block that most needs to be read cleanly. */}
        <strong style={{ display: 'block', marginBottom: 'var(--gps-s-1)' }}>
          If you feel seriously unwell, do not use this page
        </strong>
        <span style={{ display: 'block' }}>
          Call 999 for a medical emergency, or 111 if you need urgent advice and it is not an
          emergency. Contact your prescriber or your own GP if you are worried about a side effect.
        </span>
      </Alert>

      <Card style={{ marginBottom: 'var(--gps-s-4)' }}>
        <h2 style={{ fontFamily: 'var(--gps-font-display)', fontSize: '18px', margin: '0 0 8px', color: 'var(--gps-text)' }}>
          Reporting a side effect
        </h2>
        <p style={{ margin: '0 0 8px', fontSize: '14.5px', lineHeight: 1.6, color: 'var(--gps-text)' }}>
          Anyone can report a suspected side effect from a medicine directly to the UK medicines
          regulator — you do not need a clinician to do it for you, and you do not need to be certain
          the medicine caused it.
        </p>
        <p style={{ margin: 0, fontSize: '14.5px', lineHeight: 1.6, color: 'var(--gps-text)' }}>
          <a href={YELLOW_CARD_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--gps-link)' }}>
            Report through the MHRA Yellow Card scheme (opens in a new tab)
          </a>
          . Please tell us too, so the clinician looking after you knows.
        </p>
      </Card>

      {view.state === 'loading' ? <Spinner label="Loading the safety information for each treatment…" /> : null}

      {view.state === 'error' ? (
        <ErrorState
          message="Sorry — we could not load the safety information for our treatments just now. The emergency and reporting advice above still applies."
          onRetry={load}
        />
      ) : null}

      {view.state === 'ready' ? <SafetyList medications={view.medications} /> : null}

      {/* The way back to who wrote this. A LINK HOME, not a call to action: a
          reader has to be able to identify the clinic behind a page that names
          prescription-only medicines, and that is a transparency obligation
          rather than a conversion opportunity. See the file header before
          replacing this with anything that starts a purchase. */}
      <p style={{ margin: 'var(--gps-s-5) 0 0', fontSize: '14px', lineHeight: 1.6, color: 'var(--gps-muted)' }}>
        <Link to={`/${CLINICAL_SAFETY_CLINIC_SLUG}`} style={{ color: 'var(--gps-link)' }}>
          Back to the clinic home page
        </Link>
      </p>
    </PageFrame>
  );
}

/**
 * The medicines themselves.
 *
 * SPLIT OUT so the empty case has one obvious home. An empty list is NOT the
 * same as a failed read and must not look like one: it means this clinic
 * publishes nothing today, which is a true statement we are willing to make, and
 * saying it in words is the only way a reader can tell it apart from a page that
 * broke.
 *
 * IT IS ALSO WHERE "WHICH ONE IS OPEN" LIVES — see below.
 */
function SafetyList({ medications }: { medications: PublicMedicationSafety[] }) {
  /* ONE OPEN SECTION FOR THE WHOLE PAGE, HELD HERE AND NOWHERE ELSE (CTO,
     2026-08-20: "use an accordion for 4 medications and open what's relevant,
     and open only one at a time").

     WHY THE PAGE OWNS IT rather than each medicine owning its own flag, which is
     what shipped first. Per-section state cannot express "only one" — there is
     nothing in it that knows the other three exist — so every medicine could
     stand open at once and the reader was back in front of the wall of prose the
     fold was added to remove. Lifting the flag to one `openKey` makes the rule
     structural: opening a section IS closing the others, and no future edit can
     reintroduce four-at-once without deleting this state.

     NOTHING IS OPEN ON ARRIVAL, and the first medicine is deliberately NOT
     pre-opened. A pre-opened section would make the page look immediately useful
     at the cost of making an editorial claim we never meant: the catalogue is
     ordered by the clinic, not by relevance to the reader, so opening whatever
     happens to be first tells them it is the one that matters most. Closed, the
     contents list above is the first thing a reader acts on — they choose the
     medicine, we don't choose it for them. */
  const [openKey, setOpenKey] = useState<string | null>(null);

  /* "OPEN WHAT'S RELEVANT": a URL that points at a treatment opens THAT
     treatment, on load and on every later hash change.

     The contents list is real `#` anchors, and links to these anchors have
     already been shared — from the payment screen, from an email, from a
     clinician who sent somebody straight to their own medicine. Following one
     must LAND on the detail, not on a closed control the reader then has to
     notice and press; a reader who has to fight the page to reach the paragraph
     they were sent to is a reader who stops reading.

     A hash we do not recognise leaves the page exactly as it is. It must not
     close what the reader has open: an unknown fragment (a stale link to a
     medicine this clinic no longer publishes, an anchor from another page)
     carries no instruction about the section they are currently reading. */
  useEffect(() => {
    const openTargeted = () => {
      const targeted = targetedSectionId();
      const match = medications.find((medication) => sectionId(medication.productKey) === targeted);
      if (match) setOpenKey(match.productKey);
    };
    openTargeted();
    window.addEventListener('hashchange', openTargeted);
    return () => window.removeEventListener('hashchange', openTargeted);
  }, [medications]);

  /* BRING THE OPEN SECTION INTO VIEW.

     This is not decoration, and it is not only for the contents list. Because
     only one section may be open, opening one CLOSES another — and if the one
     that closed sat above it, several screens of prose vanish from above the
     reader and the section they just opened jumps up past the top of the
     viewport. The reader presses a control and the thing they asked for leaves
     the screen. Re-anchoring on the section that is now open is what makes the
     accordion usable at all, which is why it runs for every open, however it was
     asked for — the contents list, the medicine's own control, or a hash.

     REDUCED MOTION COMES FROM ui/motion, NOT FROM A SECOND `matchMedia` CALL
     HERE. One signal, one place to change it, no chance of this page drifting
     out of step with the rest of the estate. `queryReducedMotion()` (the plain
     read) rather than the hook: the answer is only needed at the instant we
     scroll, and reading it here means a reader who changes the OS setting cannot
     trigger a re-render that scrolls the page from under them.

     `?.()` because jsdom implements no layout and so defines no
     `scrollIntoView` — the same optional call the pharmacy detail panel uses. A
     missing scroll is a cosmetic loss; a `TypeError` in an effect would take the
     page down. */
  useEffect(() => {
    if (openKey === null) return;
    document.getElementById(sectionId(openKey))?.scrollIntoView?.({
      block: 'start',
      behavior: queryReducedMotion() ? 'auto' : 'smooth',
    });
  }, [openKey]);

  if (medications.length === 0) {
    return (
      <Card>
        <p style={{ margin: 0, fontSize: '14.5px', lineHeight: 1.6, color: 'var(--gps-text)' }}>
          There is no treatment safety information published for this clinic at the moment. Please
          contact us and we will send you the information for your treatment.
        </p>
      </Card>
    );
  }

  return (
    <>
      {/* A CONTENTS LIST, not decoration. This page is long by nature — every
          medicine, every topic, in full — and the reader who needs it most is
          usually looking for one specific thing. In-page anchors also give
          keyboard and screen-reader users a way past several screens of prose
          they did not come for.

          THESE STAY REAL `<a href="#…">` ANCHORS AND MUST NOT BECOME BUTTONS.
          A button cannot be opened in a new tab, cannot be copied, and cannot be
          sent to somebody — and links to these very anchors have already gone
          out. Turning them into buttons would silently break every one of them.

          THE `onClick` DOES NOT REPLACE THE ANCHOR, it covers the one case the
          anchor cannot reach on its own: when the URL ALREADY ends in this
          section's id, following the link changes nothing, so no `hashchange`
          fires and the effect above never runs. That is not a corner case — it
          is exactly what happens when a reader arrives on a link to a medicine,
          closes the detail, and then clicks that medicine in the contents list
          to open it again. Without this handler, the page would answer them by
          doing nothing at all. No `preventDefault`: the address bar must still
          end up pointing at the section, because that is the URL they may copy
          or share next.

          AND IT STANDS DOWN FOR A CLICK THAT IS NOT OPENING THIS TAB. A
          ctrl/cmd/shift/alt-click, or a non-primary button, asks the browser to
          open the link SOMEWHERE ELSE — a new tab, a new window — and leaves
          this document exactly where it was. React still fires `onClick` for
          those, so without this guard a reader who cmd-clicks a treatment to
          read it alongside what they already have open would find the tab they
          were reading in had silently closed that medicine's detail, opened a
          different one and scrolled away from their place. That is the precise
          failure the paragraph above says these anchors exist to prevent, and
          the handler added to help them was causing it. The new tab needs no
          help from here: it mounts fresh with the fragment in its URL and the
          hash effect opens the right medicine. Keyboard activation (Enter on a
          focused link) arrives with no modifiers and `button === 0`, so it is
          untouched by this test and still opens the section. */}
      <nav aria-label="Treatments on this page" style={{ marginBottom: 'var(--gps-s-4)' }}>
        <h2 style={{ fontFamily: 'var(--gps-font-display)', fontSize: '18px', margin: '0 0 8px', color: 'var(--gps-text)' }}>
          Treatments covered on this page
        </h2>
        <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '14.5px', lineHeight: 1.7 }}>
          {medications.map((medication) => (
            <li key={medication.productKey}>
              <a
                href={`#${sectionId(medication.productKey)}`}
                onClick={(event) => {
                  if (opensElsewhere(event)) return;
                  setOpenKey(medication.productKey);
                }}
                style={{ color: 'var(--gps-link)' }}
              >
                {medication.medicationName}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
        {medications.map((medication) => (
          <MedicationSection
            key={medication.productKey}
            medication={medication}
            open={openKey === medication.productKey}
            /* Pressing the open section's own control closes it, so a reader can
               always put the page back the way they found it — an accordion in
               which something is always open gives them no way to stop reading. */
            onToggle={() =>
              setOpenKey((current) => (current === medication.productKey ? null : medication.productKey))
            }
          />
        ))}
      </div>
    </>
  );
}

/**
 * One medicine, in full.
 *
 * HEADING LEVELS ARE LOAD-BEARING: `h2` for the medicine, `h3` for the two
 * blocks inside it, and the shared `PatientSafetyTopics` emits `h4` for each
 * topic. An unbroken outline is how a screen-reader user moves through several
 * screens of clinical prose; skipping a level to get a font size is how that
 * navigation quietly stops working.
 *
 * WHETHER IT IS OPEN IS NOT THIS COMPONENT'S BUSINESS (2026-08-20). It is told
 * `open` and it reports presses; {@link SafetyList} decides which single section
 * that is. A medicine that kept its own flag could not know about the other
 * three, which is exactly how four of them ended up open at once.
 */
function MedicationSection({
  medication,
  open,
  onToggle,
}: {
  medication: PublicMedicationSafety;
  open: boolean;
  onToggle: () => void;
}) {
  const id = sectionId(medication.productKey);
  const headingId = `${id}-heading`;
  const detailId = `${id}-detail`;
  const safety = medication.safety;

  return (
    <Card>
      {/* `aria-labelledby` ties the whole block to the medicine's name, so a
          reader who lands on any part of it — including one of the several
          same-named urgent-action lists on this page — is told which treatment
          they are reading about.

          `gps-safety-anchor` is what stops a jump to this section landing its
          top edge underneath the sticky ribbon + header — see tokens.css. It
          matters for the ANCHOR as much as for the accordion's own scroll: the
          reader would arrive looking at a list of red flags with the name of the
          medicine they belong to hidden above the fold. */}
      <section id={id} className="gps-safety-anchor" aria-labelledby={headingId}>
        <h2
          id={headingId}
          style={{
            fontFamily: 'var(--gps-font-display)',
            fontSize: '20px',
            lineHeight: 1.25,
            letterSpacing: '-0.01em',
            margin: '0 0 12px',
            color: 'var(--gps-text)',
          }}
        >
          {medication.medicationName}
        </h2>

        {/* THE VERSION IS THE PROVENANCE OF THE WORDS ON THIS PAGE, and it says
            so in a sentence (CTO, 2026-08-20: "last reviewed date is not
            important, just add a version number" — so a version, and no date).

            WHY publish it at all: it is what an acknowledgement is recorded
            against (SafetyAcknowledgement.version), so it is the only thing that
            lets anybody — a patient, a clinician, a regulator — match the words
            on this page to the words a particular person agreed to. Wording
            without a version cannot be matched to anything a year later.

            WHY IT IS NO LONGER A LABEL/VALUE ROW, which is what shipped first
            ("Information version" · `ui/sections` `Row`). That component is the
            estate's grammar for an ATTRIBUTE OF THE THING NAMED ABOVE IT — it is
            how the dashboard prints Treatment / Plan term / Price and how the
            dispensing panel prints the clinical rows. A short label on the left
            and a bold figure on the right, one line under a medicine's name, is
            read as a fact about the MEDICINE. On a page about medicines the
            nearest available readings of a number in that slot are a dose, a
            strength or a batch number, and a patient acting on any of those
            would be acting on a number that describes a document. That is not
            untidy, it is dangerous, and no amount of care in the label text
            fixes a grammar that says "attribute of this medicine".

            A sentence cannot be misread that way, because it names what it is a
            version OF before it gives the number, and it is set in the muted
            secondary size that marks publishing metadata rather than clinical
            content. The version stays in its own element so it remains findable
            as a discrete value. */}
        <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '13.5px', lineHeight: 1.5, color: 'var(--gps-muted)' }}>
          Version of this safety information:{' '}
          <span style={{ color: 'var(--gps-text)', fontWeight: 600 }}>{safety.version}</span>
        </p>
        {/* NO CLINICAL SIGN-OFF ROW ON THIS PAGE, AND THAT IS NOT AN OVERSIGHT
            (CTO, 2026-08-20). Contracts requires `clinicallyApproved` to be
            surfaced — but it says "to STAFF", and it means it: the flag tells a
            prescriber whether the wording a patient was shown has been ratified,
            so that they cover the risks themselves if it has not. That reader is
            served by clinicians/SafetyReview and clinicians/SafetySignoffNotice,
            both of which still render it.
            To a PATIENT the same words say something we never meant: "awaiting
            review by our clinical lead", printed above the risks of the medicine
            they are taking, reads as a warning that the safety information itself
            may be wrong. It is internal editorial state, and publishing it to the
            person least able to act on it buys nothing and frightens them.
            If you are removing it from the CLINICIAN surfaces, stop — that is the
            obligation this page is exempt from, not a duplicate of it. */}

        {/* WHAT COLLAPSES, AND WHAT NEVER DOES (CTO, 2026-08-20). The CTO asked
            for the detail behind a button — their words on the urgent block were
            "I'm happy to have this part, then there should be a button to get
            more information covering Pancreatitis, Dehydration and your
            kidneys…". So the TOPICS fold away and the URGENT-ACTION LINES DO
            NOT, for EVERY medicine, open or closed.

            That split is the whole safety argument for collapsing anything on
            this page at all, and the accordion did not change it — it made it
            matter more, because now at most one medicine's detail is open and
            the other three are represented ONLY by these lines. Somebody may
            arrive here at 2am because something is happening to them right now,
            and "stop treatment and seek urgent medical attention" is useless
            behind a control they have to find first. Four medicines of prose, by
            contrast, is exactly what makes the page hard to read at any other
            time. If you are tempted to fold the red flags in too so the page
            looks tidier: that is the failure this comment exists to stop. */}
        <div style={{ marginTop: 'var(--gps-s-3)' }}>
          {/* THE SAME COMPONENT THE PAYMENT SCREEN MOUNTS. Not a copy of it —
              see the file header, and MedicationSafetyGate's own. */}
          <PatientUrgentActions redFlags={safety.redFlags} />
        </div>

        {/* A REAL button with `aria-expanded` + `aria-controls`, not a styled div
            and not a bare `<details>`: the label has to change with the state so
            it always describes what pressing it will do, and it names the
            medicine so that four identical "Read more" controls on one page are
            distinguishable out of context (WCAG 2.4.4).

            IT SITS OUTSIDE THE REGION IT CONTROLS, AND THAT IS LOAD-BEARING NOW
            THAT SECTIONS CLOSE EACH OTHER. Opening one medicine unmounts another
            medicine's detail without that reader having asked for it, so nothing
            a keyboard user can be standing on may live inside the part that
            disappears: the control stays mounted through every open and close,
            keeps its identity, and only its label flips. That is why closing a
            section can never strand focus on a removed element and why this
            component needs no focus-restoring dance to compensate. Moving this
            button inside the collapsible `div` — or adding a second "hide"
            control at the foot of the detail — would drop a keyboard user back
            at the top of the document mid-read. */}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={detailId}
          className={buttonClass('ghost')}
          style={{
            ...buttonStyle('ghost'),
            width: 'auto',
            minHeight: '44px',
            padding: '10px 16px',
            marginTop: 'var(--gps-s-3)',
          }}
        >
          {open ? 'Hide full safety information' : 'Read the full safety information'}
          <span className="gps-sr-only"> for {medication.medicationName}</span>
        </button>

        {/* Unmounted rather than hidden with CSS. The topics are several screens
            of prose per medicine; leaving four copies in the tree costs a
            screen-reader user a long walk past content they collapsed on
            purpose, and `hidden` alone has historically been overridden by a
            stray `display` rule. */}
        {open ? (
          <div id={detailId}>
            <h3 style={{ fontFamily: 'var(--gps-font-display)', fontSize: '17px', margin: 'var(--gps-s-3) 0 8px', color: 'var(--gps-text)' }}>
              Full safety information
            </h3>
            <div style={{ fontSize: '14px', lineHeight: 1.6, color: 'var(--gps-text)' }}>
              <PatientSafetyTopics topics={safety.topics} />
            </div>
          </div>
        ) : null}

        <h3 style={{ fontFamily: 'var(--gps-font-display)', fontSize: '17px', margin: 'var(--gps-s-3) 0 8px', color: 'var(--gps-text)' }}>
          Reporting a side effect from this treatment
        </h3>
        {/* The patient's own reporting route, in the wording the payment screen
            uses, repeated per medicine rather than only once at the top: a
            reader who followed an anchor straight to their own treatment never
            saw the top of this page. */}
        <PatientYellowCardLine yellowCardUrl={safety.yellowCardUrl} />

        <PatientExternalSources links={safety.externalLinks} />
      </section>
    </Card>
  );
}
